/*
 * @Author: Mr.mjc
 * @Date: 2022-06-27 19:02:37
 * @LastEditors: MJC
 * @LastEditTime: 2025-10-23 10:23:10
 * @Description:
 * @FilePath: /next-ui/packages/wui-table/src/FilterType.tsx
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import omit from "omit.js"
import DatePicker from '../../wui-datepicker/src';
import TimePicker from '../../wui-timepicker/src';
import InputNumber from '../../wui-input-number/src';
import Input from '../../wui-input/src';
import Select from '../../wui-select/src';
import Switch from '../../wui-switch/src/index'
import FilterDropDown from './FilterDropDown';
import { IFilterTypeProps } from './iTable';
import { InputNumberDefaultProps } from '../../wui-input-number/src/iInputNumber';
import { InputProps } from '../../wui-input/src/iInput';
import { SelectProps } from '../../wui-select/src/iSelect';
import { DatePickerProps, RangePickerProps } from '../../wui-datepicker/src/iPicker';
import { TimePickerProps } from '../../wui-timepicker/src/iTimePicker';
import { SwitchProps } from '../../wui-switch/src/iSwitch';

// @ts-ignore
const { RangePicker, YearPicker, MonthPicker, WeekPicker } = DatePicker;

const FilterType: React.FC<IFilterTypeProps> = (props) => {
    const {
        filterDropdown = 'show',
        filterDropdownType,
        onFilterClear,
        onFilterChange,
        onChange,
        onSelectDropdown,
        filterInputNumberOptions,
        filterDropdownIncludeKeys,
        dataIndex,
        format,
        className,
        clsPrefix,
        locale,
        fieldid,
        dir: direction,
        rendertype,
        filterDropdownOptions
    } = props;

    // 初始化状态
    const [value, setValue] = useState("");
    const [open, setOpen] = useState(false);
    const [condition, setCondition] = useState(filterDropdownType === 'string' ? 'LIKE' : 'EQ');
    const [timeOpen, setTimeOpen] = useState(false);

    // 使用 ref 存储控制值
    const controlValueRef = useRef(filterDropdownOptions?.value || '');
    const controlConditionRef = useRef(
        filterDropdownOptions?.conditionValue || (filterDropdownType === 'string' ? 'LIKE' : 'EQ')
    );

    // 更新控制值
    useEffect(() => {
        if (filterDropdownOptions?.value !== undefined) {
            controlValueRef.current = filterDropdownOptions.value || '';
        }
        if (filterDropdownOptions?.conditionValue !== undefined) {
            controlConditionRef.current = filterDropdownOptions.conditionValue ||
                (filterDropdownType === 'string' ? 'LIKE' : 'EQ');
        }
    }, [filterDropdownOptions?.value, filterDropdownOptions?.conditionValue, filterDropdownType]);

    /**
     * 清除过滤条件
     */
    const clearFilter = useCallback(() => {
        if (value !== "") {
            setValue("");
            setCondition(filterDropdownType === 'string' ? 'LIKE' : 'EQ');
            // 调用清除方法参数为当前字段的field
            onFilterClear && dataIndex && onFilterClear(dataIndex);
        }
    }, [value, filterDropdownType, onFilterClear, dataIndex]);

    /**
     * 设置输入文本的值
     */
    const changeText = useCallback((val: string) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlValueRef.current = val;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
        } else {
            setValue(val);
            onFilterChange && onFilterChange(dataIndex, val, condition);
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 输入框回车执行回调
     */
    const changeTextCall = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        const { onChange } = filterDropdownOptions || {};
        if (e.keyCode === 13) {
            const inputValue = (e.target as HTMLInputElement).value;
            if (onChange) {
                controlValueRef.current = inputValue;
                onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
            } else {
                inputValue !== "" && onFilterChange && onFilterChange(dataIndex, inputValue, condition);
            }
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 下拉条件的回调
     */
    const handleSelectDropdown = useCallback((item: Record<string, any>) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlConditionRef.current = item.key;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
        } else {
            setCondition(item.key);
            if (value !== "") {
                onFilterChange && onFilterChange(dataIndex, value, item.key);
            }
        }
    }, [filterDropdownOptions, dataIndex, value, onFilterChange]);

    /**
     * 修改数值型的值
     */
    const changeNumber = useCallback((val: string) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlValueRef.current = val;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
        } else {
            setValue(val);
            onFilterChange && onFilterChange(dataIndex, val, condition);
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 设置下拉值
     */
    const changeSelect = useCallback((val: string) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlValueRef.current = val;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
        } else {
            if (onFilterChange) {
                onFilterChange(dataIndex, val, condition);
                setValue(val);
            }
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 设置日期值
     */
    const changeDate = useCallback((val: any) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlValueRef.current = val;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
            setOpen(false);
        } else {
            if (onFilterChange) {
                onFilterChange(dataIndex, val, condition);
                setValue(val);
                setOpen(false);
            }
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 设置时间值
     */
    // @ts-ignore 函数参数校验
    const changeTime = useCallback((time: any, timeString: string) => {
        const { onChange } = filterDropdownOptions || {};
        if (onChange) {
            controlValueRef.current = timeString;
            onChange && onChange(dataIndex, controlValueRef.current, controlConditionRef.current);
            setTimeOpen(false);
        } else {
            if (onFilterChange) {
                onFilterChange(dataIndex, timeString, condition);
                setValue(timeString);
                setTimeOpen(false);
            }
        }
    }, [filterDropdownOptions, dataIndex, onFilterChange, condition]);

    /**
     * 根据不同的类型生成对应的组件类型包含一些参数的适应
     */
    const renderControl = useMemo(() => {
        const showTime = true;
        const fieldIdAttr = fieldid === undefined ? {} : { fieldid: `${fieldid}-${rendertype}` };
        const currentConditionValue = filterDropdownOptions?.conditionValue || controlConditionRef.current;

        switch (rendertype) {
            case 'text':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <Input
                            value={value}
                            className={className}
                            onKeyDown={changeTextCall}
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<InputProps>}
                            onChange={changeText}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowClear={value}
                            isShowCondition={filterDropdown}
                            filterDropdownType={filterDropdownType}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'number':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <InputNumber
                            className={className}
                            value={value}
                            iconStyle="one"
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<InputNumberDefaultProps>}
                            {...(filterInputNumberOptions as Partial<InputNumberDefaultProps>)}
                            onChange={changeNumber}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowClear={Number(value) != 0}
                            isShowCondition={filterDropdown}
                            filterDropdownType={filterDropdownType}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'dropdown':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <Select
                            {...props}
                            locale={props.locale as string}
                            dataIndex={props.dataIndex as number}
                            size="md"
                            dir={direction}
                            value={value}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<SelectProps>}
                            onChange={changeSelect}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownType={filterDropdownType}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'date':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <DatePicker
                            {...props}
                            value={value}
                            open={open}
                            format={format}
                            locale={locale as string}
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<DatePickerProps>}
                            onChange={changeDate}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownType={filterDropdownType}
                            {...fieldIdAttr}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                        />
                    </div>
                );

            case 'dateyear':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <YearPicker
                            {...props}
                            value={value}
                            open={open}
                            format={format}
                            locale={locale as string}
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<DatePickerProps>}
                            onChange={changeDate}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownType={filterDropdownType}
                            {...fieldIdAttr}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                        />
                    </div>
                );

            case 'datemonth':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <MonthPicker
                            {...props}
                            value={value}
                            open={open}
                            format={format}
                            locale={locale as string}
                            direction={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<DatePickerProps>}
                            onChange={changeDate}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownType={filterDropdownType}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'dateweek':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <WeekPicker
                            {...props}
                            value={value}
                            open={open}
                            format={format}
                            locale={locale as string}
                            direction={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<DatePickerProps>}
                            onChange={changeDate}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownType={filterDropdownType}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'daterange':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <RangePicker
                            {...props}
                            value={value}
                            open={open}
                            format={format}
                            showTime={showTime as any}
                            locale={locale as string}
                            direction={direction}
                            placeholder={'开始 ~ 结束'}
                            dateInputPlaceholder={['开始', '结束']}
                            allowClear
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<RangePickerProps>}
                            onChange={changeDate}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'time':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <TimePicker
                            {...omit(props, ['clsPrefix'])}
                            value={value}
                            format={format}
                            locale={locale as string}
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<TimePickerProps>}
                            onChange={changeTime}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            dataIndex={dataIndex}
                            dataText={value}
                            onSelectDropdown={handleSelectDropdown}
                            onClickClear={clearFilter}
                            isShowCondition={filterDropdown}
                            isShowClear={value}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            case 'bool':
                return (
                    <div className={`${clsPrefix} filter-wrap`}>
                        <Switch
                            className={className}
                            onChange={onChange}
                            dir={direction}
                            {...fieldIdAttr}
                            {...filterDropdownOptions as Partial<SwitchProps>}
                        />
                        <FilterDropDown
                            locale={locale}
                            dir={direction}
                            onSelectDropdown={onSelectDropdown}
                            filterDropdownIncludeKeys={filterDropdownIncludeKeys}
                            conditionValue={currentConditionValue}
                            {...fieldIdAttr}
                        />
                    </div>
                );

            default:
                return <div></div>;
        }
    }, [
        rendertype, fieldid, clsPrefix, className, value, open, timeOpen,
        direction, locale, dataIndex, filterDropdown, filterDropdownType,
        filterDropdownIncludeKeys, filterDropdownOptions, filterInputNumberOptions,
        format, props, onChange, onSelectDropdown,
        changeText, changeTextCall, handleSelectDropdown, clearFilter,
        changeNumber, changeSelect, changeDate, changeTime
    ]);

    return (
        <div data-filter-type='filterContext'>
            {renderControl}
        </div>
    );
};

FilterType.defaultProps = {
    filterDropdown: 'show'
};

export default FilterType;